import React from 'react';
import { Box, Typography, Paper } from '@mui/material';

export interface EmptyStateProps {
  /**
   * Icon to display (typically an MUI icon or emoji)
   */
  icon: React.ReactNode;
  /**
   * Main title text
   */
  title: string;
  /**
   * Descriptive message explaining the empty state
   */
  message: string;
  /**
   * Action buttons or links (typically Button components)
   */
  actions?: React.ReactNode;
  /**
   * Whether to show a Paper wrapper
   * @default false
   */
  contained?: boolean;
  /**
   * Size variant
   * @default "medium"
   */
  size?: 'small' | 'medium' | 'large';
}

const sizeConfig = {
  small: { iconSize: 32, titleVariant: 'subtitle1' as const, spacing: 1 },
  medium: { iconSize: 48, titleVariant: 'h6' as const, spacing: 2 },
  large: { iconSize: 64, titleVariant: 'h5' as const, spacing: 3 },
};

/**
 * Reusable empty state component for displaying when no data is available.
 * Use this for zero-data states with helpful call-to-action buttons.
 *
 * @example
 * // Basic usage with icon and actions
 * <EmptyState
 *   icon={<ComputerIcon sx={{ fontSize: 48, color: 'text.secondary' }} />}
 *   title="No exporters yet"
 *   message="Add your first exporter to start monitoring"
 *   actions={
 *     <Button variant="contained" startIcon={<AddIcon />}>
 *       Add Exporter
 *     </Button>
 *   }
 * />
 *
 * @example
 * // Multiple action buttons
 * <EmptyState
 *   icon={<WebsiteIcon sx={{ fontSize: 48 }} />}
 *   title="No websites monitored"
 *   message="Add websites to monitor uptime and SSL certificates"
 *   actions={
 *     <Box sx={{ display: 'flex', gap: 1 }}>
 *       <Button variant="contained">Add Website</Button>
 *       <Button variant="outlined">Learn More</Button>
 *     </Box>
 *   }
 * />
 *
 * @example
 * // Contained in a Paper (for use inside tables/cards)
 * <EmptyState
 *   icon={<InboxIcon />}
 *   title="No results"
 *   message="Try adjusting your search filters"
 *   contained
 *   size="small"
 * />
 */
export function EmptyState({
  icon,
  title,
  message,
  actions,
  contained = false,
  size = 'medium',
}: EmptyStateProps) {
  const config = sizeConfig[size];

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: config.spacing * 2,
        px: config.spacing,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: config.spacing,
          color: 'text.secondary',
          '& > svg': {
            fontSize: config.iconSize,
          },
        }}
      >
        {icon}
      </Box>

      <Typography
        variant={config.titleVariant}
        fontWeight="bold"
        gutterBottom
        color="text.primary"
      >
        {title}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: actions ? config.spacing : 0, maxWidth: 400 }}
      >
        {message}
      </Typography>

      {actions && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          {actions}
        </Box>
      )}
    </Box>
  );

  if (contained) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {content}
      </Paper>
    );
  }

  return content;
}

export default EmptyState;
